import type { DeviceMeta } from '../types';

const TEXT_ENCODER = new TextEncoder();
const KEY_STORAGE = 'limen-lab.v1.device-secret';

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function hex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function sha256Hex(value: string): Promise<string> {
  return hex(await crypto.subtle.digest('SHA-256', TEXT_ENCODER.encode(value)));
}

export async function getDeviceSecret(): Promise<string> {
  const existing = localStorage.getItem(KEY_STORAGE);
  if (existing) return bytesToBase64(base64ToBytes(existing));
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const encoded = bytesToBase64(bytes);
  localStorage.setItem(KEY_STORAGE, encoded);
  return encoded;
}

function colorGamut(): DeviceMeta['colorGamut'] {
  if (matchMedia('(color-gamut: rec2020)').matches) return 'rec2020';
  if (matchMedia('(color-gamut: p3)').matches) return 'p3';
  if (matchMedia('(color-gamut: srgb)').matches) return 'srgb';
  return 'unknown';
}

function pointer(): DeviceMeta['pointer'] {
  if (matchMedia('(pointer: coarse)').matches) return 'coarse';
  if (matchMedia('(pointer: fine)').matches) return 'fine';
  if (matchMedia('(pointer: none)').matches) return 'none';
  return 'unknown';
}

function viewportBucket(): string {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const wb = w < 600 ? 'xs' : w < 900 ? 'sm' : w < 1280 ? 'md' : 'lg';
  const hb = h < 600 ? 'short' : h < 900 ? 'mid' : 'tall';
  return `${wb}-${hb}`;
}

export async function collectDeviceMeta(audioSampleRate?: number): Promise<DeviceMeta> {
  const secret = await getDeviceSecret();
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    deviceHash: await sha256Hex(`limen-lab:v1:${secret}`),
    userAgentHash: await sha256Hex(navigator.userAgent),
    colorGamut: colorGamut(),
    pointer: pointer(),
    viewportBucket: viewportBucket(),
    deviceMemory: nav.deviceMemory,
    hardwareConcurrency: navigator.hardwareConcurrency,
    audioSampleRate,
    reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
    platform: navigator.platform || 'unknown'
  };
}

export function resetDeviceIdentity(): void {
  localStorage.removeItem(KEY_STORAGE);
}
